const SURNAME_PARTICLES = new Set([
  "da",
  "das",
  "de",
  "del",
  "di",
  "dos",
  "la",
  "las",
  "los",
  "van",
  "von",
  "y",
]);

function text(value, fallback = "") {
  return typeof value === "string" && value.trim()
    ? value.trim().replace(/\s+/g, " ")
    : fallback;
}

function authorParts(value) {
  if (value && typeof value === "object") {
    const literal = text(value.literal || value.name);
    if (literal) return { literal, family: literal, given: "" };
    const family = text(value.family || value.lastName || value.surname);
    const given = text(value.given || value.firstName || value.givenNames);
    return {
      literal: "",
      family: family || given || "Autor no informado",
      given,
    };
  }

  const name = text(value, "Autor no informado");
  if (name.includes(",")) {
    const [family, ...given] = name.split(",");
    return {
      literal: "",
      family: text(family, name),
      given: text(given.join(" ")),
    };
  }

  const tokens = name.split(" ").filter(Boolean);
  if (tokens.length === 1) return { literal: name, family: name, given: "" };
  let familyStart = tokens.length - 1;
  while (
    familyStart > 0
    && SURNAME_PARTICLES.has(tokens[familyStart - 1].toLocaleLowerCase("es-AR"))
  ) {
    familyStart -= 1;
  }
  return {
    literal: "",
    family: tokens.slice(familyStart).join(" "),
    given: tokens.slice(0, familyStart).join(" "),
  };
}

function initials(value) {
  return text(value)
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => {
      const letters = part.match(/[\p{L}]/gu) || [];
      return letters[0] ? `${letters[0].toLocaleUpperCase("es-AR")}.` : "";
    })
    .filter(Boolean)
    .join(" ");
}

function apaAuthor(value) {
  const parts = authorParts(value);
  if (parts.literal) return parts.literal;
  const givenInitials = initials(parts.given);
  return givenInitials ? `${parts.family}, ${givenInitials}` : parts.family;
}

function authorList(citation) {
  const authors = citation?.metadata?.authors;
  if (Array.isArray(authors) && authors.length) return authors;
  return ["Autor no informado"];
}

function joinAuthors(values) {
  const authors = values.map(apaAuthor);
  if (authors.length === 1) return authors[0];
  if (authors.length === 2) return `${authors[0]}, y ${authors[1]}`;
  return `${authors.slice(0, -1).join(", ")}, y ${authors.at(-1)}`;
}

function shortAuthors(values) {
  const surnames = values.map((author) => authorParts(author).family);
  if (surnames.length === 1) return surnames[0];
  if (surnames.length === 2) return `${surnames[0]} y ${surnames[1]}`;
  return `${surnames[0]} et al.`;
}

function publicationYear(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return String(value.getUTCFullYear());
  }
  const match = String(value || "").trim().match(/^(\d{4})/u);
  return match?.[1] || "s. f.";
}

function dateValueText(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  return text(value);
}

function editionText(value) {
  const edition = text(value);
  if (!edition || /^1(?:\.|\s|ª|a\b)/iu.test(edition)) return "";
  return edition.replace(/\bedici[oó]n\b/iu, "ed.");
}

function locatorText(value) {
  const locator = text(value);
  if (!locator) return "";
  if (/^p{1,2}\./iu.test(locator)) return locator;
  return locator;
}

function stablePublicationUrl(citation) {
  const metadata = citation?.metadata || {};
  const doi = text(metadata.doi || metadata.DOI);
  if (doi) {
    return doi.startsWith("http")
      ? doi
      : `https://doi.org/${doi.replace(/^doi:\s*/iu, "")}`;
  }
  return text(
    metadata.publicationUrl
    || metadata.canonicalUrl
    || metadata.stableUrl,
  );
}

export function citationType(citation = {}) {
  return citation.type || citation.citationType || citation.category || "source";
}

export function apaReferenceParts(citation = {}) {
  if (citationType(citation) !== "doctrine") return null;
  const metadata = citation.metadata || {};
  const title = text(
    metadata.title || citation.source?.title || metadata.source?.title || citation.label,
    "Obra sin título",
  );
  return {
    authors: joinAuthors(authorList(citation)),
    shortAuthors: shortAuthors(authorList(citation)),
    year: publicationYear(metadata.publishedAt),
    title,
    edition: editionText(metadata.edition || metadata.editionLabel),
    publisher: text(
      metadata.publisher
      || citation.source?.organization
      || metadata.source?.organization,
      "Editorial no informada",
    ),
    url: stablePublicationUrl(citation),
  };
}

export function formatApaReference(citation = {}) {
  const parts = apaReferenceParts(citation);
  if (!parts) return "";
  return [
    `${parts.authors} (${parts.year}).`,
    `${parts.title}${parts.edition ? ` (${parts.edition})` : ""}.`,
    `${parts.publisher}.`,
    parts.url,
  ].filter(Boolean).join(" ");
}

export function formatApaParentheticalCitation(citation = {}) {
  const parts = apaReferenceParts(citation);
  if (!parts) return "";
  const locator = locatorText(citation.locator);
  return `(${[parts.shortAuthors, parts.year, locator].filter(Boolean).join(", ")})`;
}

export function formatApaNarrativeCitation(citation = {}) {
  const parts = apaReferenceParts(citation);
  if (!parts) return "";
  const locator = locatorText(citation.locator);
  return `${parts.shortAuthors} (${parts.year})${locator ? `, ${locator}` : ""}`;
}

export function formatLegalReference(citation = {}) {
  const label = text(citation.label, "Fuente registrada");
  const locator = locatorText(citation.locator);
  return locator && locator !== label ? `${label}, ${locator}` : label;
}

export function citationReferenceIdentity(citation = {}) {
  const metadata = citation.metadata || {};
  return text(
    citation.evidenceId
    || citation.id
    || metadata.editionId
    || metadata.bookId
    || citation.sourceId,
    `${citationType(citation)}:${text(citation.label, "source")}:${text(citation.locator)}`,
  ).slice(0, 1000);
}

export function normalizeCitationReference(citation = {}) {
  const type = citationType(citation);
  const source = citation.source || citation.metadata?.source || {};
  const metadata = citation.metadata || {};
  const normalized = {
    id: citationReferenceIdentity(citation),
    evidenceId: text(citation.evidenceId || citation.id) || null,
    type,
    style: type === "doctrine" ? "apa7" : "legal",
    label: text(citation.label || citation.reference, "Fuente registrada"),
    locator: text(citation.locator) || null,
    quoteText: text(citation.quoteText || citation.exactText).slice(0, 2500) || null,
    sourceId: text(citation.sourceId) || null,
    source: {
      title: text(source.title) || null,
      organization: text(source.organization) || null,
      url: text(source.url) || null,
      tribunaleaUrl: text(source.tribunaleaUrl || citation.tribunaleaUrl) || null,
      official: source.official === true,
    },
    metadata: {
      bookId: text(metadata.bookId) || null,
      editionId: text(metadata.editionId) || null,
      title: text(metadata.title) || null,
      authors: Array.isArray(metadata.authors) ? metadata.authors : [],
      publisher: text(metadata.publisher) || null,
      edition: text(metadata.edition || metadata.editionLabel) || null,
      isbn: text(metadata.isbn) || null,
      publishedAt: dateValueText(metadata.publishedAt) || null,
      doi: text(metadata.doi || metadata.DOI) || null,
      publicationUrl: text(
        metadata.publicationUrl || metadata.canonicalUrl || metadata.stableUrl,
      ) || null,
      official: metadata.official === true,
      validFrom: text(metadata.validFrom) || null,
      validTo: text(metadata.validTo) || null,
    },
  };
  return {
    ...normalized,
    shortCitation: type === "doctrine"
      ? formatApaParentheticalCitation(normalized)
      : formatLegalReference(normalized),
    narrativeCitation: type === "doctrine"
      ? formatApaNarrativeCitation(normalized)
      : formatLegalReference(normalized),
    bibliography: type === "doctrine"
      ? formatApaReference(normalized)
      : formatLegalReference(normalized),
  };
}

export function normalizeCitationReferences(citations = []) {
  const references = [];
  const seen = new Set();
  for (const citation of citations || []) {
    if (!citation || typeof citation !== "object") continue;
    const normalized = normalizeCitationReference(citation);
    if (seen.has(normalized.id)) continue;
    seen.add(normalized.id);
    references.push(normalized);
  }
  return references;
}

export function bibliographicReferenceKey(citation) {
  if (!citation || typeof citation !== "object") return "";
  const reference = normalizeCitationReference(citation);
  const metadata = reference.metadata || {};
  return text(
    metadata.editionId
    || metadata.bookId
    || reference.sourceId,
    `${reference.style}:${reference.bibliography}`,
  );
}

export function uniqueBibliographicReferences(citations = []) {
  const references = normalizeCitationReferences(citations);
  const seen = new Set();
  return references
    .filter((reference) => {
      const key = bibliographicReferenceKey(reference);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => left.bibliography.localeCompare(
      right.bibliography,
      "es-AR",
      { sensitivity: "base" },
    ));
}

export function removeBibliographicReference(citations = [], target) {
  const targetKey = bibliographicReferenceKey(target);
  const references = normalizeCitationReferences(citations);
  if (!targetKey) return references;
  return references.filter((reference) => (
    bibliographicReferenceKey(reference) !== targetKey
  ));
}

const BIBLIOGRAPHY_SECTION_HEADING = /(?:^|\n)[\t ]*(?:referencias(?:[\t ]+bibliogr[aá]ficas)?|bibliograf[ií]a|references|bibliography)[\t ]*:?[\t ]*(?=\n|$)/iu;

export function hasBibliographySection(value) {
  return BIBLIOGRAPHY_SECTION_HEADING.test(
    String(value || "").replace(/\r\n?/gu, "\n"),
  );
}

export function appendReferencesToPlainText(value, citations = []) {
  const body = typeof value === "string" ? value.trim() : "";
  if (hasBibliographySection(body)) return body;
  const references = uniqueBibliographicReferences(citations);
  if (!references.length) return body;
  return [
    body,
    "Referencias",
    ...references.map(({ bibliography }) => bibliography),
  ].filter(Boolean).join("\n\n");
}

function claimWithCitations(claim, citations) {
  const suffix = citations.join(" ");
  const match = String(claim).match(/^(.*?)([.!?])$/su);
  return match
    ? `${match[1]} ${suffix}${match[2]}`
    : `${claim} ${suffix}`;
}

export function appendDoctrineCitationsToAnswer(answer = {}, evidence = []) {
  if (!answer?.answer || !Array.isArray(answer.claims)) return answer;
  const byId = new Map((evidence || []).map((item) => [item.evidenceId, item]));
  let content = answer.answer;
  for (const claim of answer.claims) {
    const claimText = text(claim?.text);
    if (!claimText || !content.includes(claimText)) continue;
    const citations = [...new Set((claim.evidenceIds || [])
      .map((id) => byId.get(id))
      .filter((item) => citationType(item) === "doctrine")
      .map((item) => formatApaParentheticalCitation(item))
      .filter(Boolean))];
    if (!citations.length) continue;
    const claimIndex = content.indexOf(claimText);
    const localContext = content.slice(
      claimIndex,
      claimIndex + claimText.length + citations.join(" ").length + 8,
    );
    if (citations.every((citation) => localContext.includes(citation))) continue;
    content = content.replace(claimText, claimWithCitations(claimText, citations));
  }
  return { ...answer, answer: content };
}
